import axios from 'axios';
import { apiClient } from './api';
import { getMySquads } from './squads';
import type {
  BuzzFeedV1Response,
  BuzzLiveRefreshResponse,
  CommunityDashboardSnapshotResponse,
  HomeDashboardResponse,
} from '../types';

type PlayHistoryItem = {
  id: number;
  squadId: number;
  squadName?: string | null;
  title?: string | null;
  scoreCompactSummary?: string | null;
  courtName?: string | null;
  startTime?: string | null;
  completedAt?: string | null;
  gamesCount?: number | null;
};

type PlayHistoryResponse = {
  sessions?: PlayHistoryItem[] | null;
};

type BuzzAction = {
  url?: string | null;
};

type BuzzActions = {
  primary?: BuzzAction | null;
  secondary?: BuzzAction[] | null;
};

type BuzzSource = {
  sourceLabel?: string | null;
};

type BuzzMedia = {
  thumbnailUrl?: string | null;
};

type BuzzItem = {
  itemId?: string | null;
  headline?: string | null;
  summary?: string | null;
  source?: BuzzSource | null;
  sport?: string | null;
  itemType?: string | null;
  media?: BuzzMedia | null;
  actions?: BuzzActions | null;
  publishedAt?: string | null;
};

type BuzzBlock = {
  items?: BuzzItem[] | null;
};

type BuzzTop = {
  pulseStrip?: BuzzBlock | null;
  liveRail?: BuzzBlock | null;
  hero?: BuzzBlock | null;
};

type SnapshotBuzzFeedResponse = {
  top?: BuzzTop | null;
  body?: BuzzBlock[] | null;
};

export type WebBuzzFeedParams = {
  view?: string;
  sport?: 'ALL' | 'TENNIS' | 'PICKLEBALL' | 'BOTH';
  scope?: 'ALL' | 'LOCAL' | 'STATE' | 'NATIONAL' | 'INTERNATIONAL';
  density?: string;
  cursor?: string | null;
  signal?: AbortSignal;
  telemetry?: {
    trigger?: string;
    screenVisible?: boolean;
  };
};

export type WebBuzzLiveRefreshParams = {
  limit?: number;
  sport?: 'ALL' | 'TENNIS' | 'PICKLEBALL' | 'BOTH';
  scope?: 'ALL' | 'LOCAL' | 'STATE' | 'NATIONAL' | 'INTERNATIONAL';
  signal?: AbortSignal;
  telemetry?: {
    trigger?: string;
    screenVisible?: boolean;
  };
};

export async function getHomeDashboard(): Promise<HomeDashboardResponse> {
  const response = await apiClient.get<HomeDashboardResponse>('/api/v1/web/home/dashboard');
  return response.data;
}

export async function getCommunityDashboardSnapshot(): Promise<CommunityDashboardSnapshotResponse> {
  try {
    const response = await apiClient.get<CommunityDashboardSnapshotResponse>('/api/v1/web/home/community-snapshot');
    return response.data;
  } catch (error) {
    if (!shouldFallbackToClientSnapshot(error)) {
      throw error;
    }

    return buildCommunityDashboardSnapshotFromStableApis();
  }
}

function shouldFallbackToClientSnapshot(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  return status === 404 || status === 500 || status === 501 || status === 503;
}

async function buildCommunityDashboardSnapshotFromStableApis(): Promise<CommunityDashboardSnapshotResponse> {
  const homeDashboard = await getHomeDashboard();

  const [squadsResult] = await Promise.allSettled([
    getMySquads(),
  ]);

  const squads = squadsResult.status === 'fulfilled' ? squadsResult.value : [];

  return {
    greetingName: homeDashboard.greetingName,
    membershipsCount: homeDashboard.membershipsCount,
    provider: homeDashboard.provider ?? null,
    squads: buildSquadSummary(squads),
    play: buildPlaySummary(null),
    buzz: buildBuzzSummary(null),
  };
}

function buildSquadSummary(
  squads: Awaited<ReturnType<typeof getMySquads>>
): CommunityDashboardSnapshotResponse['squads'] {
  const nonDmSquads = squads.filter((squad) => squad.squadType !== 'DM');
  const communitySquads = nonDmSquads.filter((squad) => squad.tenantId == null);
  const visibleSquads = communitySquads.length > 0 ? communitySquads : nonDmSquads;
  const ordered = [...visibleSquads].sort((left, right) =>
    String(right.lastMessageAt ?? '').localeCompare(String(left.lastMessageAt ?? ''))
  );

  const unreadCount = ordered.reduce((sum, squad) => sum + Number(squad.unreadCount ?? 0), 0);
  const manageableCount = ordered.reduce(
    (sum, squad) => sum + (isManageableSquadRole(squad.userRole) ? 1 : 0),
    0
  );

  return {
    totalCount: ordered.length,
    manageableCount,
    unreadCount,
    items: ordered.slice(0, 4).map((squad) => ({
      squadId: squad.id,
      name: squad.name,
      sport: squad.sport ?? squad.sportType ?? null,
      privacyType: squad.privacyType ?? squad.privacy ?? null,
      tenantId: squad.tenantId ?? null,
      linkedProgramId: squad.linkedProgramId ?? null,
      userRole: squad.userRole ?? null,
      memberCount: squad.memberCount,
      unreadCount: squad.unreadCount ?? 0,
      lastMessage: squad.lastMessage ?? null,
      lastMessageSender: squad.lastMessageSender ?? null,
      lastMessageAt: squad.lastMessageAt ?? null,
    })),
  };
}

function isManageableSquadRole(role?: string | null): boolean {
  switch ((role || '').toUpperCase()) {
    case 'OWNER':
    case 'ADMIN':
    case 'MODERATOR':
      return true;
    default:
      return false;
  }
}

function buildPlaySummary(playHistory: PlayHistoryResponse | null): CommunityDashboardSnapshotResponse['play'] {
  const sessions = playHistory?.sessions ?? [];
  const recentResults = sessions.slice(0, 4).map((session) => ({
    sessionId: session.id,
    squadId: session.squadId,
    squadName: session.squadName ?? null,
    title: session.title || session.squadName || 'Scored session',
    scoreCompactSummary: session.scoreCompactSummary ?? null,
    courtName: session.courtName ?? null,
    startTime: session.startTime ?? null,
    completedAt: session.completedAt ?? null,
  }));

  const latestRecordedAt =
    sessions[0]?.completedAt ?? sessions[0]?.startTime ?? null;

  const recentCourtsTracked = sessions.reduce(
    (sum, session) => sum + Math.max(Number(session.gamesCount ?? 0), 1),
    0
  );

  return {
    recentScorecardsCount: sessions.length,
    recentCourtsTracked,
    latestRecordedAt,
    recentResults,
  };
}

function buildBuzzSummary(feed: SnapshotBuzzFeedResponse | null): CommunityDashboardSnapshotResponse['buzz'] {
  if (!feed) {
    return {
      title: 'Buzz now',
      subtitle: 'Local tennis and pickleball stories show up here.',
      liveItemCount: 0,
      featuredItem: null,
      liveItems: [],
      items: [],
    };
  }

  const liveItems = new Map<string, CommunityDashboardSnapshotResponse['buzz']['items'][number]>();
  const feedItems = new Map<string, CommunityDashboardSnapshotResponse['buzz']['items'][number]>();

  addBuzzItems(liveItems, feed.top?.pulseStrip?.items ?? [], true, 3);
  addBuzzItems(liveItems, feed.top?.liveRail?.items ?? [], true, 3);

  addBuzzItems(feedItems, feed.top?.hero?.items ?? [], false, 4);
  for (const block of feed.body ?? []) {
    addBuzzItems(feedItems, block.items ?? [], false, 4);
    if (feedItems.size >= 4) {
      break;
    }
  }

  const featuredItem = [...feedItems.values()][0] ?? [...liveItems.values()][0] ?? null;
  const liveItemCount = (feed.top?.pulseStrip?.items?.length ?? 0) + (feed.top?.liveRail?.items?.length ?? 0);

  return {
    title: 'Buzz now',
    subtitle:
      liveItemCount > 0
        ? 'Live and local tennis and pickleball storylines right now.'
        : 'The latest local tennis and pickleball storylines.',
    liveItemCount,
    featuredItem,
    liveItems: [...liveItems.values()],
    items: [...feedItems.values()],
  };
}

function addBuzzItems(
  target: Map<string, CommunityDashboardSnapshotResponse['buzz']['items'][number]>,
  items: BuzzItem[],
  live: boolean,
  limit: number
) {
  for (const item of items) {
    const itemId = item.itemId?.trim();
    if (!itemId || target.has(itemId)) {
      continue;
    }

    target.set(itemId, {
      itemId,
      headline: item.headline || 'Buzz update',
      summary: item.summary ?? null,
      sourceLabel: item.source?.sourceLabel ?? null,
      sport: item.sport ?? null,
      itemType: item.itemType ?? null,
      imageUrl: item.media?.thumbnailUrl ?? null,
      destinationUrl: resolveBuzzDestination(item.actions ?? null),
      publishedAt: item.publishedAt ?? null,
      live,
    });

    if (target.size >= limit) {
      return;
    }
  }
}

function resolveBuzzDestination(actions: BuzzActions | null): string | null {
  if (actions?.primary?.url?.trim()) {
    return actions.primary.url;
  }

  const fallback = actions?.secondary?.find((action) => action.url?.trim());
  return fallback?.url ?? null;
}

export async function getWebBuzzFeed({
  view = 'for_you',
  sport = 'ALL',
  scope = 'LOCAL',
  density,
  cursor,
  signal,
  telemetry,
}: WebBuzzFeedParams = {}): Promise<BuzzFeedV1Response> {
  const response = await apiClient.get<BuzzFeedV1Response>('/api/v1/web/buzz/feed', {
    params: {
      view,
      sport: sport !== 'ALL' ? sport.toLowerCase() : undefined,
      scope: scope.toLowerCase(),
      density,
      cursor: cursor || undefined,
    },
    signal,
    headers: {
      ...(telemetry?.trigger ? { 'X-Buzz-Trigger': telemetry.trigger } : {}),
      ...(typeof telemetry?.screenVisible === 'boolean'
        ? { 'X-Buzz-Screen-Visible': String(telemetry.screenVisible) }
        : {}),
    },
  });

  return response.data;
}

export async function getWebBuzzLiveRefresh({
  limit = 24,
  sport = 'ALL',
  scope = 'LOCAL',
  signal,
  telemetry,
}: WebBuzzLiveRefreshParams = {}): Promise<BuzzLiveRefreshResponse> {
  const response = await apiClient.get<BuzzLiveRefreshResponse>('/api/v1/web/buzz/live/refresh', {
    params: {
      limit,
      sport: sport !== 'ALL' ? sport.toLowerCase() : undefined,
      scope: scope.toLowerCase(),
    },
    signal,
    headers: {
      ...(telemetry?.trigger ? { 'X-Buzz-Trigger': telemetry.trigger } : {}),
      ...(typeof telemetry?.screenVisible === 'boolean'
        ? { 'X-Buzz-Screen-Visible': String(telemetry.screenVisible) }
        : {}),
    },
  });

  return response.data;
}
